import React, { useState, useRef, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { isDeviceSelector } from 'common/selectors';
import { useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export * from 'components/simple-dropdown/simple-dropdown-element';

export const SimpleDropDown = ({
  className,
  flexGrow,
  icon,
  iconRight,
  title,
  left,
  children,
  'data-e2e': dataE2e,
  'aria-label': ariaLabel,
}) => {
  const isDevice = useSelector(isDeviceSelector);
  const [isItemsShown, showItems] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const menuItemsRef = useRef([]);

  useEffect(() => {
    const handleFocusOut = (event) => {
      if (isItemsShown && dropdownRef.current && menuRef.current) {
        const focusLeftDropdown = !dropdownRef.current.contains(event.target) && 
                                 !menuRef.current.contains(event.target);

        if (focusLeftDropdown) {
          showItems(false);
        }
      }
    };

    document.addEventListener('focusin', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusOut);
    };
  }, [isItemsShown]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isItemsShown) return;

      switch (event.key) {
        case 'Escape':
          showItems(false);
          dropdownRef.current?.focus();
          event.preventDefault();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex(prev => {
            const next = prev + 1 >= menuItemsRef.current.length ? 0 : prev + 1;
            menuItemsRef.current[next]?.focus();
            return next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex(prev => {
            const next = prev - 1 < 0 ? menuItemsRef.current.length - 1 : prev - 1;
            menuItemsRef.current[next]?.focus();
            return next;
          });
          break;
        default:
          break;
      }
    };

    if (isItemsShown) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isItemsShown]);

  useEffect(() => {
    setActiveIndex(-1);
    if (isItemsShown) {
      menuItemsRef.current[0]?.focus();
      setActiveIndex(0);
    }
  }, [isItemsShown]);

  const show = () => {
    if (!isDevice) {
      showItems(true);
    }
  };

  const hide = () => {
    if (!isDevice) {
      showItems(false);
    }
  };

  const toggle = (e) => {
    if (isDevice) {
      e.preventDefault();
      showItems(!isItemsShown);
    }
  };


  const handleMenuFocusOut = (event) => {
    if (event.relatedTarget === dropdownRef.current || !menuRef.current?.contains(event.relatedTarget)) {
      showItems(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      showItems(false);
      event.preventDefault();
    } else if (event.key === 'Tab' && isItemsShown) {
      return;
    } else if ((event.key === 'Enter' || event.key === 'Space') && !isItemsShown) {
      event.preventDefault();
      showItems(true);
    }
  };

  const titleClass = className ? className : 'ct-simple-dropdown__title';
  const titleHoverClass = isItemsShown ? ` ${titleClass}--hover` : '';

  return (
    <div data-e2e={dataE2e} className={`ct-simple-dropdown${flexGrow ? ' ct-simple-dropdown--flex-grow' : ''}`}>
      <button
        className={`${titleClass}${titleHoverClass}`}
        aria-label={ariaLabel}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isItemsShown}
        ref={dropdownRef}
      >
        {icon && <FontAwesomeIcon icon={icon} />}

        {title && <span className={'ct-simple-dropdown__title-text'}>{title}</span>}

        {iconRight && <FontAwesomeIcon icon={iconRight} />}

        {isDevice && <input className="ct-simple-dropdown__transparent-input" type="text" disabled />}
      </button>

      {isItemsShown && (
        <div
          className="ct-simple-dropdown__hook ct-simple-dropdown__hook--visible"
          role="menu"
          aria-orientation="vertical"
          ref={menuRef}
          onMouseEnter={show}
          onMouseLeave={hide}
          onBlur={handleMenuFocusOut}
        >
          <div
            className={`ct-simple-dropdown__list-container${
              left ? ' ct-simple-dropdown__list-container--left' : ' ct-simple-dropdown__list-container--right'
            }`}
          >
            <ul className="ct-simple-dropdown__list" data-e2e="dropdown-list">
              {React.Children.map(children, (child, index) => {
                if (child?.type?.name === 'SimpleDropDownElement') {
                  return React.cloneElement(child, {
                    role: 'menuitem',
                    ref: (el) => (menuItemsRef.current[index] = el),
                    'aria-selected': index === activeIndex,
                    className: `${child.props.className || ''} ${index === activeIndex ? 'ct-simple-dropdown__item--active' : ''}`.trim()
                  });
                }
                return child && React.cloneElement(child, {
                    role: 'menuitem',
                    'aria-selected': index === activeIndex,
                  className: `${child.props.className || ''} ${index === activeIndex ? 'ct-simple-dropdown__item--active' : ''}`.trim()
                });
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

SimpleDropDown.propTypes = {
  icon: PropTypes.object,
  iconRight: PropTypes.object,
  title: PropTypes.string,
  flexGrow: PropTypes.bool,
  className: PropTypes.string,
};